import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import {
  Camera, MapPin, ShieldCheck, Calculator, ChartLineUp, FileText,
  Lightning, ArrowRight, Buildings, Users, Pulse,
} from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0D0D10] text-white overflow-x-hidden">
      {/* Top nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0D0D10]/70 border-b border-[#2D2D35]/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors">Platform</a>
            <a href="#workflow" className="hover:text-white transition-colors">How it works</a>
            <a href="#built-for" className="hover:text-white transition-colors">Built for</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/staff" data-testid="cta-staff-nav"
                  className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white border border-[#2D2D35] hover:border-zinc-500 px-3 h-9 rounded-md inline-flex items-center">
              Staff app
            </Link>
            <Link to="/hr" data-testid="cta-hr-nav"
                  className="text-xs uppercase tracking-widest bg-[#E50914] hover:bg-[#C80812] text-white px-4 h-9 rounded-md inline-flex items-center gap-1">
              HR sign in <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="absolute inset-x-0 top-0 h-[700px] pointer-events-none"
             style={{ background: "radial-gradient(ellipse at 30% 0%, rgba(229,9,20,0.15), transparent 60%)" }} />
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#E50914] mb-6 rise">
              <Pulse size={14} weight="fill" /> Workforce intelligence · v2.0
            </div>
            <h1 className="font-display text-5xl sm:text-6xl xl:text-7xl tracking-tight leading-[0.95] rise">
              The HR platform<br/>
              built for the way<br/>
              <span className="text-[#E50914]">teams actually work.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 mt-7 max-w-xl leading-relaxed rise">
              Field-grade attendance, geo-fence verification, payroll engine and audit-grade
              compliance — purpose-built for founders running 50–5,000 person hybrid teams.
            </p>
            <div className="flex flex-wrap gap-3 mt-9 rise">
              <Link to="/hr" data-testid="cta-hr-hero"
                    className="bg-[#E50914] hover:bg-[#C80812] text-white px-6 h-12 rounded-md inline-flex items-center gap-2 font-semibold tracking-wide transition-colors">
                Open HR portal <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/staff" data-testid="cta-staff-hero"
                    className="border border-[#2D2D35] hover:border-zinc-500 text-white px-6 h-12 rounded-md inline-flex items-center gap-2 font-medium transition-colors">
                Staff app sign-in
              </Link>
            </div>
            <div className="flex items-center gap-8 mt-12 text-xs text-zinc-500">
              <Bullet>JWT secured</Bullet>
              <Bullet>Audit-logged</Bullet>
              <Bullet>Mobile-first</Bullet>
              <Bullet>Indian payroll-ready</Bullet>
            </div>
          </div>

          {/* Floating product mock */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-10 bg-[#E50914]/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative bg-[#16161C] border border-[#2D2D35] rounded-2xl p-6 shadow-2xl rise">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#E50914]">Live</div>
                  <div className="font-display text-lg mt-1">Today · Operations</div>
                </div>
                <div className="text-[10px] tracking-widest text-zinc-500 font-mono">02:25 IST</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Present" value="146" color="#22C55E" />
                <Stat label="Late" value="07" color="#F59E0B" />
                <Stat label="Absent" value="03" color="#FF3B30" />
                <Stat label="Verified" value="138" color="#22C55E" />
                <Stat label="Disputes" value="01" color="#F59E0B" />
                <Stat label="Hours" value="1,184" />
              </div>
              <div className="mt-5 bg-[#0D0D10] border border-[#2D2D35] rounded-md p-3 text-[11px] font-mono space-y-1.5">
                <Row dot="#22C55E" left="02:25 · ssam (E12) IN" right="Plankalamukku · 12m" />
                <Row dot="#22C55E" left="02:23 · ravi.k (E08) IN" right="HQ · 4m" />
                <Row dot="#F59E0B" left="02:18 · neha.s (E05) IN" right="HQ · LATE 18m" />
                <Row dot="#22C55E" left="02:10 · arun (E02) IN" right="Site-A · 8m" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="px-6 py-24 border-t border-[#2D2D35]/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#E50914] mb-3">Platform</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Everything HR needs.<br/>Nothing it doesn't.</h2>
            </div>
            <p className="text-sm text-zinc-400 max-w-md">
              Eleven modules. One platform. Designed for the founder who can't afford another vendor patchwork.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2D2D35] border border-[#2D2D35] rounded-xl overflow-hidden">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-[#0D0D10] p-8 hover:bg-[#16161C] transition-colors">
                <f.icon size={28} weight="bold" className="text-[#E50914]" />
                <div className="font-display text-xl mt-5">{f.title}</div>
                <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for */}
      <section id="built-for" className="px-6 py-24 border-t border-[#2D2D35]/60">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#E50914] mb-3">Built for</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight">
              Founders running real,<br/>on-ground operations.
            </h2>
            <p className="text-zinc-400 mt-6 leading-relaxed">
              EthicHR replaces 5+ vendors (attendance, payroll, leave, compliance, performance)
              with one clean platform. Especially for healthcare, logistics, field-services, retail
              chains and any hybrid team where "did they actually show up?" matters.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Persona icon={Buildings} title="Founders & MDs"
              body="Single dashboard of who's on, where, since when. Audit-grade salary slips with one click." />
            <Persona icon={Users} title="HR managers"
              body="Verify selfies, approve leaves, run payroll — both auto and manual — and export every fine, advance and bonus." />
            <Persona icon={MapPin} title="Field supervisors"
              body="Live geo-fence compliance map. Catch ghost-punches and route violations before they cost a customer." />
            <Persona icon={Pulse} title="Finance teams"
              body="Productivity-linked incentives, advance/loan recovery and approval workflow — straight into payroll." />
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="px-6 py-24 border-t border-[#2D2D35]/60">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#E50914] mb-3">Workflow</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-12">From punch to payslip in one platform.</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#2D2D35] border border-[#2D2D35] rounded-xl overflow-hidden">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-[#0D0D10] p-8">
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#E50914]">STEP / {String(i+1).padStart(2,"0")}</div>
                <div className="font-display text-lg mt-4">{s.title}</div>
                <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-28 border-t border-[#2D2D35]/60 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(ellipse at 70% 50%, rgba(229,9,20,0.18), transparent 65%)" }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-6xl tracking-tight leading-tight">
            Bring your workforce<br/><span className="text-[#E50914]">into one platform.</span>
          </h2>
          <p className="text-zinc-400 mt-6 max-w-xl mx-auto">
            Built for teams of 50 to 5,000. White-glove onboarding, custom shift rules, and a payroll engine that handles real Indian payroll edge cases.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/hr" data-testid="cta-hr-bottom"
                  className="bg-[#E50914] hover:bg-[#C80812] text-white px-8 h-12 rounded-md inline-flex items-center gap-2 font-semibold tracking-wide">
              Sign in to HR portal <ArrowRight size={16} weight="bold" />
            </Link>
            <a href="mailto:hello@ethichr.in"
               className="border border-[#2D2D35] hover:border-zinc-500 text-white px-8 h-12 rounded-md inline-flex items-center gap-2 font-medium">
              Talk to founder
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2D2D35]/60 px-6 py-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="flex items-center gap-4">
            <Logo size="sm" />
            <span>© {new Date().getFullYear()} EthicHR. All rights reserved.</span>
          </div>
          <div className="flex gap-5">
            <a href="mailto:hello@ethichr.in" className="hover:text-white">hello@ethichr.in</a>
            <Link to="/staff" className="hover:text-white">Staff app</Link>
            <Link to="/hr" className="hover:text-white">HR portal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  { icon: Camera, title: "Live selfie attendance", desc: "Camera-only punches with GPS + employee, time and shift watermarked into every photo." },
  { icon: MapPin, title: "Geo-fence verification", desc: "Approved sites, allowed radius, haversine distance. Auto-flags suspicious punches." },
  { icon: ShieldCheck, title: "Dispute-mode timeline", desc: "Every event, photo and map pin in one HR-friendly drawer. End he-said-she-said." },
  { icon: Calculator, title: "Manual & auto payroll", desc: "Calendar grid for daily-wage teams, attendance-driven engine for salaried staff." },
  { icon: ChartLineUp, title: "Productivity incentives", desc: "Pay extra for jobs above target — automatically computed, included in salary slip." },
  { icon: FileText, title: "Branded PDF slips", desc: "Company profile, logo, signature, stamp. Two report variants — consolidated and detailed." },
  { icon: Lightning, title: "Fines · Bonuses · Advances", desc: "Twelve deduction categories, nine bonus types, loan-installment recovery — all in payroll." },
  { icon: Users, title: "Leaves & approvals", desc: "Five leave types, multi-step approval workflow, real balance with carry-forward." },
  { icon: Pulse, title: "Audit-grade everything", desc: "Every action logged. Restricted detailed report for founders & MDs only." },
];

const STEPS = [
  { title: "Staff punches in", desc: "Selfie + GPS + chosen reporting site. Watermark burned in." },
  { title: "Geo-fence verifies", desc: "Within radius → VALID. Outside → MISMATCH. Beyond → SUSPICIOUS." },
  { title: "HR reviews", desc: "Drawer shows photo, map, accuracy, distance, every event of the day." },
  { title: "Payroll runs", desc: "Auto or manual. Productivity, deductions, advances. Salary slip in one click." },
];

function Stat({ label, value, color = "#FFFFFF" }) {
  return (
    <div className="bg-[#0D0D10] border border-[#2D2D35] rounded-md p-3">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
function Row({ dot, left, right }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      <span className="flex-1 text-zinc-300 truncate">{left}</span>
      <span className="text-zinc-500">{right}</span>
    </div>
  );
}
function Bullet({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-1 h-1 rounded-full bg-[#E50914]" /> {children}
    </span>
  );
}
function Persona({ icon: Icon, title, body }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-6 hover:bg-[#1C1C24] transition-colors">
      <Icon size={22} weight="bold" className="text-[#E50914]" />
      <div className="font-display text-lg mt-4">{title}</div>
      <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{body}</div>
    </div>
  );
}
